// Import the events from the theme if available
const EVENTS = window.EVENTS || {
  variantChange: 'variant:change',
  ajaxProductAdded: 'ajaxProduct:added',
  ajaxProductError: 'ajaxProduct:error'
};

// Sticky Add to Cart Component
class StickyAddToCart {
  constructor() {
    this.init();
  }

  init() {
    // Only run on product pages
    if (!document.querySelector('.template-product')) return;

    this.originalBuyButtons = document.querySelector('.block-buy-buttons');
    this.stickyAddToCart = document.getElementById('sticky-add-to-cart');
    
    if (!this.originalBuyButtons || !this.stickyAddToCart) return;

    // Get important data attributes from the original buy buttons
    this.productId = this.originalBuyButtons.dataset.productId;
    this.sectionId = this.originalBuyButtons.dataset.sectionId;
    this.cartType = this.originalBuyButtons.dataset.cartType;
    
    // Set up the sticky add to cart form
    this.setupStickyForm();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initial check for visibility
    this.toggleStickyAddToCart();
  }

  setupStickyForm() {
    // Get the form from the sticky add to cart
    this.stickyForm = this.stickyAddToCart.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.stickyForm) return;
    
    // Get the original form
    this.originalForm = this.originalBuyButtons.querySelector(`#product-form-${this.sectionId}`);
    if (!this.originalForm) return;
    
    // Sync the variant ID initially
    const originalVariantInput = this.originalForm.querySelector('input[name="id"]');
    const stickyVariantInput = this.stickyForm.querySelector('input[name="id"]');
    
    if (originalVariantInput && stickyVariantInput) {
      stickyVariantInput.value = originalVariantInput.value;
    }
  }

  setupEventListeners() {
    // Listen for scroll events to show/hide the sticky add to cart
    window.addEventListener('scroll', this.toggleStickyAddToCart.bind(this));
    
    // Listen for variant changes to update the sticky add to cart
    document.addEventListener(
      `${EVENTS.variantChange}:${this.sectionId}:${this.productId}`,
      this.handleVariantChange.bind(this)
    );
    
    // Handle form submission
    if (this.stickyForm) {
      this.stickyForm.addEventListener('submit', this.handleSubmit.bind(this));
    }
  }

  toggleStickyAddToCart() {
    if (!this.originalBuyButtons || !this.stickyAddToCart) return;
    
    const buyButtonsRect = this.originalBuyButtons.getBoundingClientRect();
    const isOutOfView = buyButtonsRect.bottom < 0;
    
    if (isOutOfView) {
      this.stickyAddToCart.classList.add('visible');
    } else {
      this.stickyAddToCart.classList.remove('visible');
    }
  }

  handleVariantChange(event) {
    const { variant } = event.detail;
    
    if (!variant) return;
    
    // Update the variant ID in the sticky form
    const stickyVariantInput = this.stickyForm.querySelector('input[name="id"]');
    if (stickyVariantInput) {
      stickyVariantInput.value = variant.id;
    }
    
    // Update the button state (enabled/disabled)
    const originalButton = this.originalForm.querySelector('[name="add"]');
    const stickyButton = this.stickyForm.querySelector('[name="add"]');
    
    if (originalButton && stickyButton) {
      if (originalButton.hasAttribute('disabled')) {
        stickyButton.setAttribute('disabled', 'disabled');
        
        // Update button text
        const originalButtonText = originalButton.querySelector('span').textContent;
        stickyButton.querySelector('span').textContent = originalButtonText;
      } else {
        stickyButton.removeAttribute('disabled');
        
        // Update button text
        const originalButtonText = originalButton.querySelector('span').textContent;
        stickyButton.querySelector('span').textContent = originalButtonText;
      }
    }
    
    // Update price if needed
    this.updateStickyPrice(variant);
  }

  updateStickyPrice(variant) {
    if (!variant) return;
    
    const priceContainer = this.stickyAddToCart.querySelector('.sticky-add-to-cart__price');
    if (!priceContainer) return;
    
    // Format the price
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    });
    
    if (variant.compare_at_price > variant.price) {
      // Sale price
      priceContainer.innerHTML = `
        <span class="sticky-add-to-cart__price-sale">${formatter.format(variant.price / 100)}</span>
        <s class="sticky-add-to-cart__price-compare">${formatter.format(variant.compare_at_price / 100)}</s>
      `;
    } else {
      // Regular price
      priceContainer.innerHTML = `<span>${formatter.format(variant.price / 100)}</span>`;
    }
  }

  async handleSubmit(event) {
    // Don't handle if cart type is 'page'
    if (this.cartType === 'page') return;
    
    event.preventDefault();
    
    // Show loading state
    this.disableAddToCartButton();
    
    try {
      // Check if Kachang bundle is being used
      const kachingBundle = document.querySelector('kaching-bundle');
      if (kachingBundle && kachingBundle.dataset.initialized === 'true') {
        console.log('Kachang bundle detected, handling separately');
        
        // Let Kachang handle the add to cart process
        const originalAddToCartBtn = this.originalForm.querySelector('[type="submit"], button[name="add"]');
        if (originalAddToCartBtn) {
          // Don't dispatch our custom events, as Kachang will handle cart updates
          console.log('Triggering original add to cart button for Kachang bundle');
          originalAddToCartBtn.click();
          
          // Show success message
          this.showSuccessMessage();
          return;
        }
      }
      
      // Standard product handling (non-Kachang)
      const responseJson = await this.addVariantToCart();
      
      // Dispatch the same event as the original buy buttons
      document.dispatchEvent(
        new CustomEvent(EVENTS.ajaxProductAdded, {
          bubbles: true,
          detail: {
            product: responseJson
          }
        })
      );
      
      // Show success message
      this.showSuccessMessage();
    } catch (error) {
      this.handleError(error);
    } finally {
      this.enableAddToCartButton();
    }
  }

  async addVariantToCart() {
    // Get form data from the original form to ensure all options are included
    const formData = new FormData(this.originalForm);
    
    try {
      // Ensure Shopify routes are available
      if (!window.Shopify || !window.Shopify.routes) {
        console.error('Shopify routes not available');
        window.Shopify = window.Shopify || {};
        window.Shopify.routes = window.Shopify.routes || {};
        window.Shopify.routes.root = window.Shopify.routes.root || '/';
      }
      
      // Add the sections for cart drawer update
      formData.set('sections_url', `${window.Shopify.routes.root}variants/${formData.get('id')}`);
      formData.set('sections', 'cart-ajax');
      
      // Use a more robust URL construction
      const cartUrl = window.Shopify.routes.root.endsWith('/') 
        ? `${window.Shopify.routes.root}cart/add.js`
        : `${window.Shopify.routes.root}/cart/add.js`;
      
      console.log('Attempting to add to cart with URL:', cartUrl);
      
      const response = await fetch(cartUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        credentials: 'same-origin' // Include cookies for cross-site requests
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response from cart/add.js:', errorData);
        throw errorData;
      }
      
      return response.json();
    } catch (error) {
      console.error('Error in addVariantToCart:', error);
      throw error;
    }
  }

  handleError(error) {
    if (!error.description) {
      console.warn(error);
      return;
    }
    
    // Dispatch the same error event as the original buy buttons
    document.dispatchEvent(
      new CustomEvent(EVENTS.ajaxProductError, {
        bubbles: true,
        detail: {
          errorMessage: error.description
        }
      })
    );
    
    // Show error message
    alert(typeof error.description === 'object' ? error.message : error.description);
  }

  disableAddToCartButton() {
    const addButton = this.stickyForm.querySelector('[name="add"]');
    if (!addButton) return;
    
    addButton.setAttribute('aria-busy', 'true');
    addButton.classList.add('btn--loading');
  }

  enableAddToCartButton() {
    const addButton = this.stickyForm.querySelector('[name="add"]');
    if (!addButton) return;
    
    addButton.removeAttribute('aria-busy');
    addButton.classList.remove('btn--loading');
  }

  showSuccessMessage() {
    // Optional: Add a success message
    const successMessage = document.createElement('div');
    successMessage.className = 'sticky-add-success';
    successMessage.textContent = 'Added to cart!';
    successMessage.style.cssText = 'position: fixed; bottom: 70px; left: 50%; transform: translateX(-50%); background: #4caf50; color: white; padding: 8px 16px; border-radius: 4px; z-index: 1000; opacity: 0; transition: opacity 0.3s;';
    document.body.appendChild(successMessage);
    
    // Show and then hide the success message
    setTimeout(() => {
      successMessage.style.opacity = '1';
      setTimeout(() => {
        successMessage.style.opacity = '0';
        setTimeout(() => {
          document.body.removeChild(successMessage);
        }, 300);
      }, 2000);
    }, 10);
  }
}

// Initialize when the DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  new StickyAddToCart();
});